import { Box, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import type { CalendarView, ISODate } from '../types';
import { computePeriodLabel } from '../utils';

type CalendarToolbarProps = {
  view: CalendarView;
  cursor: ISODate;
  weekStartsOn?: 0 | 1;
  onViewChange: (view: CalendarView) => void;
  onToday: () => void;
  onPrev: () => void;
  onNext: () => void;
  renderToolbarActions?: () => ReactNode;
  allowedViews?: CalendarView[];
};

const VIEW_LABELS: Record<CalendarView, string> = {
  week: 'Week',
  month: 'Month',
  schedule: 'List',
};

const VIEWS: CalendarView[] = ['week', 'month', 'schedule'];

const NavBtn = ({ onClick, children }: { onClick: () => void; children: ReactNode }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      width: 32,
      height: 32,
      border: 'none',
      background: 'transparent',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#444',
      fontSize: 16,
      fontFamily: 'inherit',
      '&:hover': { background: '#f0ede8' },
    }}
  >
    {children}
  </Box>
);

export function CalendarToolbar({
  view,
  cursor,
  weekStartsOn = 1,
  onViewChange,
  onToday,
  onPrev,
  onNext,
  renderToolbarActions,
  allowedViews,
}: CalendarToolbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const periodLabel = computePeriodLabel(cursor, view, weekStartsOn);
  const visibleViews = allowedViews ? VIEWS.filter((v) => allowedViews.includes(v)) : VIEWS;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 22px',
        background: '#fff',
        borderBottom: '1px solid #e8e5e0',
        flexShrink: 0,
      }}
    >
      {/* Left: Today + nav + period label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Box
          component="button"
          onClick={onToday}
          sx={{
            fontSize: 12,
            fontWeight: 500,
            padding: '6px 16px',
            border: '1px solid #d0cdc8',
            borderRadius: '18px',
            background: '#fff',
            color: '#333',
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:hover': { background: '#f5f5f3' },
          }}
        >
          Today
        </Box>

        <Box sx={{ display: 'flex', gap: '2px' }}>
          <NavBtn onClick={onPrev}>‹</NavBtn>
          <NavBtn onClick={onNext}>›</NavBtn>
        </Box>

        <Box
          sx={{
            fontSize: 18,
            fontWeight: 400,
            color: '#222',
            ml: '6px',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {periodLabel}
        </Box>
      </Box>

      {/* Right: consumer actions slot + view switcher */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {renderToolbarActions?.()}

        {/* View switcher */}
        <Box sx={{ position: 'relative', ml: '10px' }}>
          <Box
            component="button"
            ref={anchorRef}
            onClick={() => setMenuOpen((o) => !o)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '7px 14px',
              border: '1px solid #d0cdc8',
              borderRadius: '6px',
              background: '#fff',
              fontSize: 12,
              fontWeight: 500,
              color: '#333',
              cursor: 'pointer',
              fontFamily: 'inherit',
              minWidth: 110,
              justifyContent: 'space-between',
              '&:hover': { background: '#f5f5f3' },
            }}
          >
            <span>{VIEW_LABELS[view]}</span>
            <Box component="span" sx={{ fontSize: 10, color: '#888' }}>▾</Box>
          </Box>

          <Popper
            open={menuOpen}
            anchorEl={anchorRef.current}
            placement="bottom-end"
            transition
            style={{ zIndex: 20 }}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps} timeout={120}>
                <Paper
                  elevation={3}
                  sx={{
                    mt: '6px',
                    border: '1px solid #e0ddd8',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    minWidth: 160,
                    boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
                  }}
                >
                  <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                    <MenuList dense sx={{ py: '4px' }}>
                      {visibleViews.map((v) => (
                        <MenuItem
                          key={v}
                          selected={v === view}
                          onClick={() => { onViewChange(v); setMenuOpen(false); }}
                          sx={{
                            fontSize: 12,
                            color: v === view ? '#2a3a22' : '#333',
                            fontWeight: v === view ? 500 : 400,
                            display: 'flex',
                            justifyContent: 'space-between',
                            px: '14px',
                            py: '8px',
                            '&:hover': { background: '#f5f5f3' },
                            '&.Mui-selected': { background: 'transparent' },
                            '&.Mui-selected:hover': { background: '#f5f5f3' },
                          }}
                        >
                          {VIEW_LABELS[v]}
                          {v === view && (
                            <Box component="span" sx={{ color: '#2a3a22', fontSize: 13, ml: 1 }}>✓</Box>
                          )}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </Box>
    </Box>
  );
}
